import React, { useEffect, useState } from "react";
import { Spin } from "antd";
import { useForm } from "react-hook-form";
import styles from "./my-profile.module.css";
import { toast } from "react-toastify";
import CustomButton from "../../../../common/custom-button/custom-button";
import ProfileTitleHr from "../../../../components/profile-title-hr/profile-title-hr";
import { useSelector } from "react-redux";
import { getAdminDetails, updateInstitutionAdmins } from "../../../../services/admin";
import { validateDate } from "../../../../utils/format-date/format-date";
import PersonalInfo from "./components/personal-info";
import ContactInfo from "./components/contact-info";

const PROFILE_FORMS = [
  {
    label: "Personal Information",
    Component: PersonalInfo,
  },
  {
    label: "Contact Information",
    Component: ContactInfo,
  },
];

const MyProfile = () => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const user = useSelector((state) => state.user?.user);
  const { control, getValues, reset, trigger } = useForm({
    mode: "onChange",
    defaultValues: {
      fullName: "",
      gender: "",
      dob: "",
      designation: "",
      email: "",
      address: "",
      phone: "",
    },
  });

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const resp = await getAdminDetails(user?.id);
      const data = resp?.data?.data;
      setProfileData(data);
      reset(formatProfileData(data));
    } catch (err) {
      console.error("Error setting user data:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatProfileData = (data) => ({
    fullName: data?.metadata?.full_name || "",
    gender: data?.metadata?.gender ?? "",
    dob: validateDate(data?.metadata?.date_of_birth),
    designation: data?.designation ?? "",
    email: data?.email_address || "",
    address: data?.metadata?.full_address ?? "",
    phone: data?.metadata?.phone_number ?? "",
  });

  const handleCancelEdit = () => {
    setIsEditMode(false);
    if (profileData) {
      reset(formatProfileData(profileData));
    }
  };

  const handleSubmit = async () => {
    const isValid = await trigger();
    if (!isValid) return;
    
    try {
      const formData = new FormData();

      setLoading(true);
      const formValues = getValues();
      formData.append("full_name", formValues.fullName);
      formData.append("gender", formValues.gender);
      formData.append("date_of_birth", formValues.dob || "");
      formData.append("designation", formValues.designation);
      formData.append("phone_number", formValues.phone);
      formData.append("full_address", formValues.address);
      formData.append("email_address", formValues.email);

      await updateInstitutionAdmins(user?.id, formData);
      await fetchProfileData();
      setIsEditMode(false);
      toast.success("Profile updated successfully!");
    } catch (err) {
      console.log(err);
      toast.error("Failed to update profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Spin spinning={loading}>
      <div>
        <div className={styles["profile-buttons-container"]}>
          <div className={styles["profile-buttons"]}>
            {isEditMode ? (
              <>
                <CustomButton
                  type="text"
                  onClick={handleCancelEdit}
                  title="Cancel"
                  className={styles.cancelButton}
                />
                <CustomButton
                  icon={
                    <img
                      src="/assets/images/edit.svg"
                      style={{ marginTop: 2 }}
                      alt="editIcon"
                    />
                  }
                  iconPosition="start"
                  onClick={handleSubmit}
                  title="Save"
                />
              </>
            ) : (
              <CustomButton
                icon={
                  <img
                    src="/assets/images/edit.svg"
                    style={{ marginTop: 2 }}
                    alt="editIcon"
                  />
                }
                iconPosition="start"
                onClick={() => setIsEditMode(!isEditMode)}
                title="Edit Profile"
              />
            )}
          </div>
        </div>

        <div className={styles["profile-section"]}>
          {PROFILE_FORMS?.map((item, index) => {
            const Component = item?.Component;
            return (
              <div key={index}>
                <ProfileTitleHr label={item?.label} />
                <Component 
                  control={control} 
                  isEditMode={isEditMode}
                />
              </div>
            )
          })}
        </div>
      </div>
    </Spin>
  );
};

export default MyProfile;
